import styled from 'styled-components';

export const TableScrollWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};

  /* Keep the scrollbar usable on smaller screens */
  scrollbar-width: thin;
`;

export const StyledTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-family: ${({ theme }) => theme.fonts.primary};
  table-layout: auto;
`;

export const TableHead = styled.thead`
  background: ${({ theme }) => theme.landing.background};
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.landing.cardBorder};
  transition: background 0.12s ease;

  ${({ $clickable }) => $clickable && 'cursor: pointer;'}

  tbody &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
  }

  tbody &:last-child {
    border-bottom: none;
  }
`;

export const HeaderCellWithIcon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const TableHeaderCell = styled.th`
  padding: ${({ theme, $extraHeaderPadding }) =>
    $extraHeaderPadding
      ? `${theme.landingSpacing.lg} ${theme.landingSpacing.lg}`
      : `${theme.landingSpacing.sm} ${theme.landingSpacing.md}`};
  text-align: ${({ $align }) => $align || 'left'};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.landing.text};
  white-space: nowrap;
  ${({ $width }) => $width && `width: ${$width};`}
`;

export const TableBody = styled.tbody``;

export const TableDataCell = styled.td`
  padding: ${({ theme }) => theme.landingSpacing.md};
  text-align: ${({ $align }) => $align || 'left'};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.landing.text};
  vertical-align: middle;
  white-space: nowrap;
`;

export const TableFooter = styled.tfoot``;

export const TableFooterRow = styled.tr`
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};
`;

export const TableFooterCell = styled.td`
  padding: ${({ theme }) => theme.landingSpacing.md};
  background: ${({ theme }) => theme.landing.background};
`;

export const TablePaginationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  flex-wrap: wrap;
`;

export const PaginationTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.landing.text};

  strong {
    color: ${({ theme }) => theme.landing.text};
    font-weight: 600;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
`;

const paginationSideStyles = `
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const PaginationLeft = styled.div`
  ${paginationSideStyles}
`;

export const PaginationRight = styled.div`
  ${paginationSideStyles}
`;

export const PaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: ${({ theme }) => theme.landing.cardBg};
  color: ${({ theme, disabled }) => (disabled ? theme.landing.textMuted : theme.landing.text)};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: 6px ${({ theme }) => theme.landingSpacing.sm};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition:
    background 0.15s ease,
    transform 0.1s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.iconBg};
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }
`;

export const TableEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  padding: ${({ theme }) => theme.landingSpacing.xxl} ${({ theme }) => theme.landingSpacing.lg};
  text-align: center;
`;

export const EmptyStateContainer = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.landing.iconBg};
  color: ${({ theme }) => theme.landing.text};

  svg {
    width: 26px;
    height: 26px;
  }
`;

export const StyledEmptyStateText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

/* ------------------------------------------------------------------ */
/* Cell-level building blocks shared across table consumers            */
/* ------------------------------------------------------------------ */

export const CellPrimaryText = styled.div`
  color: ${({ theme }) => theme.landing.text};
`;

export const CellSecondaryText = styled.div`
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.landing.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 420px;
`;

export const MonoText = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.landing.linkColor};
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  background: ${({ theme }) => theme.landing.iconBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  color: ${({ theme }) => theme.landing.text};
  font-size: ${({ theme }) => theme.fontSizes.small};
  white-space: nowrap;
`;

const statusVariants = {
  OPEN: {
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.12)',
    border: 'rgba(239, 68, 68, 0.28)',
  },
  CLOSED: {
    color: '#16a34a',
    bg: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.28)',
  },
};

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.full};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $status }) => (statusVariants[$status] || statusVariants.closed).color};
  background: ${({ $status }) => (statusVariants[$status] || statusVariants.closed).bg};
  border: 1px solid ${({ $status }) => (statusVariants[$status] || statusVariants.closed).border};
`;

export const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
`;
